import { AxiosError, AxiosResponse } from "axios";
import {
  Configuration,
  OrderApi,
  OrderLineModelDocumentModelDocumentStatusEnum,
  FileUri,
  FileUriODataResultCollection,
  OrderModel,
  OrderModelODataResultCollection,
  CustomerAddressApi,
  CustomerAddressModelODataResultCollection,
  OrderCreateModel,
} from "api";

import {
  andFilters,
  apiResponseTransformer,
  baseApiConfig,
  invalidStatusCode,
  listOfFilters,
  orFilters,
  sortToString,
  TransformedApiResponse,
} from "./apiHelpers";
import { formatNumber, valueOr } from "util/statHelpers";
import ModeOfTransportationService from "./ModeOfTransportationService";
import {
  createDocEntryUpdateRequestValues,
  validateUpdateData,
} from "./requests/requestBuilders";
import {
  orderPatchSchema,
  OrderUpdateProperties,
} from "./requests/orderSchemas";
import filterFactories from "./filterFactories";
import {
  API_RESPONSE_BROKEN_VALIDATION_EXCEPTION_TYPE,
  API_RESPONSE_VALIDATION_EXCEPTION_TYPE,
  ApiStatusException,
  BrokenRuleValidationError,
  extractBrokenValidationError,
  SchemaValidationError,
} from "services/apiErrors";

export enum SHIPPING_STATUS {
  CANCELED = "Canceled",
  NOT_SHIPPED = "Not Shipped",
  SHIPPED = "Shipped",
}

export type CreateContractRequestOrderValues = {
  shipFrom: string;
  mode: string;
  salesTerms: string;
  shipTo: string;
  quantityPerOrder: number;
  requestedShipDate: string;
  poNumber: string;
  numberOfOrders: number;
  itemCode: string;
  unitOfMeasureCode: string;
};

class OrderService {
  private api!: OrderApi;
  private customerAddressApi: CustomerAddressApi;
  private modes: Record<string, ModeOfTransportation> | undefined;
  private modeService!: ModeOfTransportationService;

  constructor(config: Configuration = baseApiConfig) {
    this.api = new OrderApi(config);
    this.modeService = new ModeOfTransportationService(config);
    this.customerAddressApi = new CustomerAddressApi(config);
  }

  private async fetchModes() {
    this.modes = {};
    return this.modeService.listModesOfTransportation().then(({ value }) =>
      value.forEach((mode) => {
        if (mode.code && this.modes) {
          this.modes[mode.code] = mode;
        }
      })
    );
  }

  private static mapApiFileUri(fileUri: FileUri): ReleaseFile {
    return {
      category: valueOr(fileUri.Category),
      date: valueOr(fileUri.Date ? new Date(fileUri.Date) : undefined),
      filename: valueOr(fileUri.FileName),
      mimeType: valueOr(fileUri.Mime ?? "application/pdf"),
      url: fileUri.Uri ?? undefined,
    };
  }

  mapApiOrder = (order: OrderModel): OrderLineItem => {
    const getShippingStatusColumn = (_order: OrderModel) => {
      if (_order.IsCanceled) {
        return SHIPPING_STATUS.CANCELED;
      } else {
        return _order.DocumentStatus ===
          OrderLineModelDocumentModelDocumentStatusEnum.Open
          ? SHIPPING_STATUS.NOT_SHIPPED
          : SHIPPING_STATUS.SHIPPED;
      }
    };

    return {
      notShipped:
        order.DocumentStatus ===
          OrderLineModelDocumentModelDocumentStatusEnum.Open ||
        !!order.IsCanceled,
      docEntry: order.DocEntry ?? 0,
      contractNumber: order.ContractNumber,
      releaseNumber: valueOr(order.ReleaseNumber),
      quantityFormatted: valueOr(
        order.CalculatedSumQuantity
          ? formatNumber(order.CalculatedSumQuantity)
          : undefined
      ),
      shipFrom: valueOr(order.DocumentLines?.[0]?.WarehouseName),
      shipFromCode: valueOr(order.DocumentLines?.[0]?.WarehouseCode),
      shipTo: valueOr(order.ShipToCode),
      poNumber: valueOr(order.NumAtCard),
      mode:
        order.ModeOfTransportationCode && this.modes
          ? this.modes[order.ModeOfTransportationCode]
          : undefined,
      shippingStatus: getShippingStatusColumn(order),
      isDraft: order.IsDraft,
      isCanceled: !!order.IsCanceled,
      salesTermsCode: order.SalesTermsCode ?? undefined,
    };
  };

  private static buildOrdersListFilters = (
    contractNumber: number,
    searchTerm: string,
    { shipTo, mode, shipFrom }: OrdersFilters
  ) => {
    const isNumeric = /^\d+$/.test(searchTerm);
    const { contains, cast, compare, documentLine } = filterFactories;
    return andFilters([
      compare("ContractNumber").eq(contractNumber),
      searchTerm
        ? orFilters([
            // ReleaseNumber is numeric so cast the property to string.
            isNumeric ? contains(cast("ReleaseNumber"), searchTerm) : null,
            contains("ShipToCode", searchTerm),
            contains("NumAtCard", searchTerm),
            documentLine("WarehouseCode").contains(searchTerm),
          ])
        : null,
      shipTo.length > 0
        ? compare("ShipToCode").in(listOfFilters(shipTo))
        : null,
      mode.length > 0
        ? compare("ModeOfTransportationCode").in(listOfFilters(mode))
        : null,
      shipFrom.length > 0
        ? documentLine("WarehouseCode").in(listOfFilters(shipFrom))
        : null,
    ]);
  };

  async listOrders(
    contractNumber: number,
    searchTerm: string,
    filters: OrdersFilters = {
      shipFrom: [],
      shipTo: [],
      mode: [],
    },
    sort?: { key: string; direction: string }
  ): Promise<
    TransformedApiResponse<OrderModelODataResultCollection, OrderLineItem[]>
  > {
    if (!this.modes) {
      await this.fetchModes();
    }

    const getSortOrder = () => {
      // ShippingStatus is a compound sort key, otherwise return the default.
      return sort?.key == "ShippingStatus"
        ? `IsCanceled ${sort.direction}, DocumentStatus ${sort.direction}`
        : sortToString(sort);
    };

    return this.api
      .apiOrderGet({
        $filter: OrderService.buildOrdersListFilters(
          contractNumber,
          searchTerm,
          filters
        ),
        $expand: "DocumentLines",
        $orderby: getSortOrder(),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Orders not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }

        const orders: OrderLineItem[] = data.value?.map(this.mapApiOrder) ?? [];
        return {
          ...data,
          value: orders,
        };
      });
  }

  getFullOrderDetails(docEntry: number): Promise<{ order: OrderModel }> {
    return this.api
      .apiOrderDocEntryGet({
        docEntry,
        $expand: "DocumentLines",
      })
      .then(({ data, status, statusText }) => {
        if (!data) {
          throw new Error("Order not found");
        } else if (invalidStatusCode(status)) {
          throw new ApiStatusException(statusText, status);
        }

        return { order: data };
      });
  }

  getReleaseFileList(
    docEntry: number
  ): Promise<
    TransformedApiResponse<FileUriODataResultCollection, ReleaseFile[]>
  > {
    return this.api
      .apiOrderGetFileUriListDocEntryGet({
        docEntry,
        $top: "50",
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Files not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        return {
          ...data,
          value: data.value?.map(OrderService.mapApiFileUri) ?? [],
        };
      });
  }

  getValidDestinations(
    contractNumber: number,
    {
      warehouseCode,
      modeOfTransportationCode,
      salesTermsCode,
    }: {
      warehouseCode: string;
      modeOfTransportationCode: string;
      salesTermsCode: string;
    }
  ): Promise<
    TransformedApiResponse<
      CustomerAddressModelODataResultCollection,
      CustomerAddressCode[]
    >
  > {
    return this.customerAddressApi
      .apiCustomerAddressShipToAddressesContractNumberWarehouseCodeModeOfTransportationCodeSalesTermsCodeGet(
        {
          contractNumber,
          warehouseCode,
          modeOfTransportationCode,
          salesTermsCode,
        }
      )
      .then((response) =>
        apiResponseTransformer(
          response as AxiosResponse<CustomerAddressModelODataResultCollection>
        )
      )
      .then((data) => {
        if (!data) {
          throw new Error("Address(es) not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }

        return {
          ...data,
          value:
            data.value?.map((address) => ({
              code: valueOr(address.Code),
            })) ?? [],
        };
      });
  }

  async updateOrderLineItem(
    docEntry: number,
    isDraft: boolean | undefined,
    values: {
      poNumber: OrderModel["NumAtCard"];
      shipTo: OrderModel["ShipToCode"];
      cardCode: OrderModel["CardCode"];
    }
  ): Promise<{ order: OrderLineItem }> {
    const requestValues = createDocEntryUpdateRequestValues(
      docEntry,
      isDraft ?? false,
      {
        ShipToCode: values.shipTo,
        NumAtCard: values.poNumber,
        CardCode: values.cardCode,
      }
    );

    const [isValid, validator] = validateUpdateData(
      orderPatchSchema,
      requestValues
    );

    const apiPropertyToInputName = (apiPropertyName: string) => {
      const nameMap: Record<keyof OrderUpdateProperties, string> = {
        ShipToCode: "shipToCode",
        NumAtCard: "poNumber",
        CardCode: "cardCode",
      };
      return nameMap.hasOwnProperty(apiPropertyName)
        ? nameMap[apiPropertyName as keyof OrderUpdateProperties]
        : apiPropertyName;
    };

    if (!isValid) {
      throw new SchemaValidationError(
        `Values passed to update for order ${docEntry} failed validation`,
        validator.errors?.map((validationError) => {
          const instanceName = validationError.instancePath.slice(1); // instancePath beings with '/'
          return {
            ...validationError,
            propertyName: apiPropertyToInputName(instanceName),
          };
        })
      );
    }

    return this.api
      .apiOrderPatch({
        apiOrderDRAFTPatchRequest: requestValues,
      })
      .then((value) => ({ order: this.mapApiOrder(value.data) }))
      .catch(
        (
          err:
            | AxiosError<{
                Type: string;
                Detail: string;
              }>
            | AxiosError<BrokenRuleValidationError>
        ) => {
          if (err.response?.data) {
            const { Type, Detail } = err.response.data;
            if (Type === API_RESPONSE_VALIDATION_EXCEPTION_TYPE) {
              // Error message pulled from the API response. The exact formatting of the message may need adjustments as we enable editing of more fields.
              const errorRegex =
                /CServiceData::SetPropertyValue\w.* failed; (\w.*) in property '(\w.*)' of '\w.*'/g;
              const errorDetails = errorRegex.exec(Detail);
              if (errorDetails && errorDetails?.length > 2) {
                const [, message, property] = errorDetails;
                throw new SchemaValidationError(
                  `API returned a validation error when updating order ${docEntry}`,
                  [
                    {
                      message,
                      propertyName: apiPropertyToInputName(property),
                    },
                  ]
                );
              }
            } else if (Type === API_RESPONSE_BROKEN_VALIDATION_EXCEPTION_TYPE) {
              const brokenValidationError = extractBrokenValidationError(
                err as AxiosError<BrokenRuleValidationError>
              );
              if (brokenValidationError) {
                throw brokenValidationError;
              }
            }
          }
          throw err;
        }
      );
  }

  createRequestOrder(
    contractNumber: number,
    {
      values,
      customerNumber,
    }: {
      values: CreateContractRequestOrderValues;
      customerNumber: string;
    }
  ): Promise<Record<string, any>> {
    const requestValues: OrderCreateModel = {
      ContractNumber: contractNumber,
      CardCode: customerNumber,
      ItemCode: values.itemCode,
      WarehouseCode: values.shipFrom,
      ShipToCode: values.shipTo,
      SalesTermsCode: values.salesTerms,
      ModeOfTransportationCode: values.mode,
      Quantity: values.quantityPerOrder,
      UnitOfMeasureCode: values.unitOfMeasureCode,
      RequestedShipDate: values.requestedShipDate,
      NumAtCard: values.poNumber.split(","),
      NumberOfOrders: values.numberOfOrders,
    };

    return this.api
      .apiOrderCreateOrdersPost({
        apiOrderCreateOrdersPostRequest: requestValues,
      })
      .then((response) => response.data)
      .catch((err: AxiosError<BrokenRuleValidationError>) => {
        if (err.response?.data) {
          const brokenValidationError = extractBrokenValidationError(err);
          if (
            brokenValidationError?.Type ===
            API_RESPONSE_BROKEN_VALIDATION_EXCEPTION_TYPE
          ) {
            throw brokenValidationError;
          }
        } else if (err.code === "ERR_NETWORK") {
          throw new Error(err.message);
        }
        throw err;
      });
  }
}

export default OrderService;
